import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import DOMPurify from "dompurify";
import { GFG__MAP_LOGO, GFG_LOGO } from "@/constants/campaignBuilder";
import type { Recipient, Stage3EmailState } from "@/context/CampaignBuilderContext";
import { resolveAllTokens, resolveSenderDisplayName } from "@/utils/emailPreview-helper";

interface EmailPreviewProps {
  state: Stage3EmailState;
  isMobile: boolean;
  recipients?: readonly Recipient[];
  onChooseGift?: () => void;
}

export default function EmailPreview({
  state,
  isMobile,
  recipients = [],
  onChooseGift,
}: EmailPreviewProps) {
  const {
    subject,
    messageHtml,
    ctaColor,
    logoEnabled,
    logoUrl,
    logoPadding,
    bannerEnabled,
    bannerUrl,
    signatureHtml,
  } = state;

  const resolvedSubject = resolveAllTokens(subject, signatureHtml || "", recipients);
  const senderName = resolveSenderDisplayName(recipients);
  const resolvedMessageHtml = DOMPurify.sanitize(
    resolveAllTokens(
      messageHtml || "<p>Your message will appear here...</p>",
      signatureHtml || "",
      recipients,
    ),
  );

  return (
    <div
      className="mx-auto bg-white overflow-hidden transition-all duration-300"
      style={{
        width: isMobile ? 360 : 600,
        maxWidth: "100%",
        borderRadius: "8px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      }}
    >
      {/* Email header bar — subject + sender details */}
      <div className="px-6 py-3.5 border-b" style={{ borderColor: "var(--color-card-border)" }}>
        <div className="mb-2.5">
          <span className="font-lato text-[10px] font-bold uppercase tracking-[1.5px] text-graphite block mb-0.5">
            Subject Line
          </span>
          <span className="font-lato text-[16px] font-bold text-rodeo-black block wrap-break-word">
            {resolvedSubject || "Email Subject"}
          </span>
        </div>
        <div className="relative inline-block group">
          <span
            className="font-lato text-[12px] text-graphite font-semibold underline cursor-help hover:text-rodeo-black transition-colors"
            style={{ textDecorationThickness: "1px", textUnderlineOffset: "2px" }}
          >
            Sender Details
          </span>
          <div
            className="absolute left-0 top-full mt-2 bg-white rounded-lg border opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transform group-hover:translate-y-0 -translate-y-1 transition-all duration-200 z-10"
            style={{
              borderColor: "var(--color-card-border)",
              boxShadow: "0 4px 14px rgba(26,22,18,0.10)",
              padding: "12px 14px",
              minWidth: 280,
            }}
            role="tooltip"
          >
            <div className="flex gap-2 font-lato text-[12px] leading-relaxed mb-1.5">
              <span className="text-graphite font-semibold shrink-0 w-20">Sender Name</span>
              <span className="text-rodeo-black">{senderName}</span>
            </div>
            <div className="flex gap-2 font-lato text-[12px] leading-relaxed">
              <span className="text-graphite font-semibold shrink-0 w-20">From</span>
              <span className="text-rodeo-black">gifts@giftsforgood.com</span>
            </div>
          </div>
        </div>
      </div>

      {/* Logo lockup */}
      <div
        className="flex items-center justify-center gap-0 p-6"
        style={{ backgroundColor: "var(--color-white)" }}
      >
        <img
          src={GFG_LOGO}
          alt="Gifts for Good"
          style={{
            maxWidth: logoEnabled && logoUrl ? 140 : 220,
            maxHeight: logoEnabled && logoUrl ? 48 : 64,
            height: "auto",
            display: "block",
            objectFit: "contain",
          }}
        />
        {logoEnabled && logoUrl && (
          <>
            <div
              style={{
                width: 1,
                height: 36,
                backgroundColor: "var(--color-smoke)",
                margin: "0 16px",
              }}
            />
            <div style={{ padding: logoPadding }}>
              <img
                src={logoUrl}
                alt="Company logo"
                style={{ maxWidth: 140, maxHeight: 48, objectFit: "contain", display: "block" }}
              />
            </div>
          </>
        )}
      </div>

      {/* Hero banner */}
      {bannerEnabled && bannerUrl && (
        <div style={{ width: "100%", lineHeight: 0 }}>
          <img
            src={bannerUrl}
            alt="Hero banner"
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        </div>
      )}

      {/* Message body */}
      <div className="px-8 py-6">
        <div
          className="font-lato text-[16px] text-graphite leading-relaxed mb-8"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: controlled internal HTML from RTE
          dangerouslySetInnerHTML={{
            __html: resolvedMessageHtml,
          }}
        />

        {/* CTA button */}
        <div className="flex justify-center">
          <button
            type="button"
            id={onChooseGift ? "fp-email-cta" : undefined}
            className={`inline-flex items-center gap-2 px-8 py-3 rounded-btn font-lato text-[13px] font-bold tracking-[2px] uppercase text-white border-none${onChooseGift ? " cursor-pointer" : " cursor-default"}`}
            style={{ backgroundColor: ctaColor }}
            onClick={onChooseGift}
          >
            <CardGiftcardIcon sx={{ fontSize: 18 }} />
            CHOOSE A GIFT
          </button>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{ paddingTop: 24, textAlign: "center", backgroundColor: "var(--color-background)" }}
      >
        <img
          src={GFG__MAP_LOGO}
          alt="Powered by GIFTforward"
          style={{ width: "100%", height: "auto", display: "block" }}
        />
      </div>
    </div>
  );
}
